import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosAuthRefreshRequestConfig } from 'axios-auth-refresh';
import i18next from 'i18next';
import {
  AuthenticationRequest,
  AuthenticationResponse,
  ExternalAuthenticationRequest,
  ExternalAuthenticationType,
  RefreshTokenRequest,
} from 'orderme-api-integration-client';
import { addAnonymousTokenHeader, CustomerClient, instance } from '../../helpers/client';
import { refreshTokenUri } from '../../helpers/clientConfigs';
import { DecodedToken, GoogleLoginError } from '../../interfaces/interfaces';
import { RootState } from '../store';
import { jwtDecode } from 'jwt-decode';
import { TokenStorage } from '../../helpers/tokenStorage';
import Cookies from 'js-cookie';

const customerClient: CustomerClient = new CustomerClient();

export const authenticate = createAsyncThunk(
  'auth/authenticate',
  async ({ input, password }: { input: string; password: string }, thunkAPI) => {
    try {
      let authenticationRequest = {
        input: input,
        password: password,
      } as AuthenticationRequest;
      const response = await customerClient.authenticate(authenticationRequest);
      return response;
    } catch (error) {
      console.error(error);
      return thunkAPI.rejectWithValue(i18next.t('errors:badLogin'));
    }
  }
);

export const authenticateFromFacebook = createAsyncThunk(
  'auth/authenticateFromFacebook',
  async (externalAuthRequest: ExternalAuthenticationRequest, thunkAPI) => {
    try {
      const response = await customerClient.authenticateFromExternal(
        externalAuthRequest,
        ExternalAuthenticationType.Facebook
      );
      return response;
    } catch (error) {
      console.error(error);
      return thunkAPI.rejectWithValue('User is not registered with Facebook account.');
    }
  }
);

export const authenticateFromGoogle = createAsyncThunk(
  'auth/authenticateFromGoogle',
  async (externalAuthRequest: ExternalAuthenticationRequest, thunkAPI) => {
    try {
      const response = await customerClient.authenticateFromExternal(
        externalAuthRequest,
        ExternalAuthenticationType.Google
      );
      return response;
    } catch (error: any) {
      console.error(error);
      return thunkAPI.rejectWithValue({
        message: 'User is not registered with Google account',
        userGrantRequired: error.userGrantRequired === true,
      } as GoogleLoginError);
    }
  }
);

export const authenticateFromApple = createAsyncThunk(
  'auth/authenticateFromApple',
  async (externalAuthRequest: ExternalAuthenticationRequest, thunkAPI) => {
    try {
      const response = await customerClient.authenticateFromExternal(
        externalAuthRequest,
        ExternalAuthenticationType.Apple
      );
      return response;
    } catch (error) {
      console.error(error);
      return thunkAPI.rejectWithValue('User is not registered with Apple account.');
    }
  }
);

export const refreshToken = createAsyncThunk('auth/refreshToken', async (_: void, thunkAPI) => {
  try {
    var state = thunkAPI.getState() as RootState;
    const request = {
      accessToken: state.authState.accessToken,
      refreshToken: state.authState.refreshToken,
    } as RefreshTokenRequest;

    const response = await instance.post<AuthenticationResponse>(refreshTokenUri, request, {
      skipAuthRefresh: true,
    } as AxiosAuthRefreshRequestConfig);
    return response.data;
  } catch (error) {
    console.error(error);
  }
});

export const authenticateAnonymous = createAsyncThunk(
  'auth/authenticateAnonymous',
  async (authCode: string, thunkAPI) => {
    try {
      const response = await customerClient.verifyAnonymousPhoneCode(authCode);
      if (response.verified) {
        return response;
      }
    } catch (error) {
      console.error(error);
    }
    return thunkAPI.rejectWithValue('Invalid confirmation code');
  }
);

export const authCodeCookie = 'anonymous-auth-code';

interface IAnonymousLogin {
  isLoggedIn: boolean;
  authCode?: string;
  phone?: string;
}

export interface AuthState {
  accessToken: string;
  refreshToken: string;
  isLoggedIn: boolean;
  anonymousLoginInfo: IAnonymousLogin;
  isLoginRequired: boolean;
  isPasswordRestored: boolean;
  rememberMe: boolean;
  loading: boolean;
  error: string;
}

const initialState: AuthState = {
  accessToken: TokenStorage.Instance.getAccessToken(),
  refreshToken: TokenStorage.Instance.getRefreshToken(),
  isLoggedIn: false,
  anonymousLoginInfo: {} as IAnonymousLogin,
  isLoginRequired: false,
  isPasswordRestored: false,
  rememberMe: false,
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState: initialState,
  reducers: {
    removeLoginError: (state) => {
      state.error = null;
    },
    login: (state) => {
      state.isLoggedIn = true;
      state.isLoginRequired = false;
      state.isPasswordRestored = mustChangePassword(state.accessToken);
      resetAnonymousLogin(state);
    },
    logout: (state) => {
      updateTokens(state, null);
      resetAnonymousLogin(state);
      addAnonymousTokenHeader();
    },
    logoutAnonymous: (state) => {
      resetAnonymousLogin(state);
      addAnonymousTokenHeader();
    },
    forceLoginAgain: (state) => {
      updateTokens(state, null);
      state.isLoginRequired = true;
    },
    resetLoginRequired: (state) => {
      state.isLoginRequired = false;
    },
    setRememberMe: (state, action: PayloadAction<boolean>) => {
      state.rememberMe = action.payload;
    },
    reloadTokenFromStorage: (state) => {
      state.accessToken = TokenStorage.Instance.getAccessToken();
      state.refreshToken = TokenStorage.Instance.getRefreshToken();
      state.isLoggedIn = false;
      state.isLoginRequired = false;
      state.isPasswordRestored = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(authenticate.fulfilled, (state, action) => {
      updateTokens(state, action.payload);
      state.loading = false;
    });
    builder.addCase(authenticate.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(authenticate.rejected, (state, action) => {
      updateTokens(state, null);
      state.loading = false;
      state.error = action.payload as string;
    });
    builder.addCase(authenticateAnonymous.fulfilled, (state, action) => {
      updateTokens(state, null);
      state.anonymousLoginInfo.authCode = action.payload.code;
      state.anonymousLoginInfo.phone = action.payload.phone;
      state.anonymousLoginInfo.isLoggedIn = true;
      const cookie = Cookies.get(authCodeCookie);
      // Don't need to renew cookie if already exist
      if (cookie !== action.payload.code) {
        // Add cookie for 1 hour
        Cookies.set(authCodeCookie, action.payload.code, { secure: true, expires: 1 / 24 });
      }
    });
    builder.addCase(authenticateAnonymous.rejected, (state, _) => {
      resetAnonymousLogin(state);
    });
    builder.addCase(authenticateFromFacebook.fulfilled, (state, action) => {
      updateTokens(state, action.payload);
      state.loading = false;
    });
    builder.addCase(authenticateFromFacebook.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(authenticateFromFacebook.rejected, (state) => {
      updateTokens(state, null);
      state.loading = false;
    });
    builder.addCase(authenticateFromGoogle.fulfilled, (state, action) => {
      updateTokens(state, action.payload);
      state.loading = false;
    });
    builder.addCase(authenticateFromGoogle.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(authenticateFromGoogle.rejected, (state) => {
      updateTokens(state, null);
      state.loading = false;
    });
    builder.addCase(authenticateFromApple.fulfilled, (state, action) => {
      updateTokens(state, action.payload);
      state.loading = false;
    });
    builder.addCase(authenticateFromApple.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(authenticateFromApple.rejected, (state) => {
      updateTokens(state, null);
      state.loading = false;
    });
    builder.addCase(refreshToken.fulfilled, (state, action) => {
      updateTokens(state, action.payload);
      state.loading = false;
    });
    builder.addCase(refreshToken.rejected, (state) => {
      updateTokens(state, null);
      state.loading = false;
    });
  },
});

function updateTokens(state: AuthState, response: AuthenticationResponse) {
  if (response) {
    state.refreshToken = response.refreshToken;
    state.accessToken = response.accessToken;
    state.isLoggedIn = true;
    TokenStorage.Instance.update(response.accessToken, response.refreshToken, state.rememberMe);
    resetAnonymousLogin(state);
  } else {
    state.refreshToken = null;
    state.accessToken = null;
    state.isLoggedIn = false;
    TokenStorage.Instance.clear();
    addAnonymousTokenHeader();
  }

  state.isLoginRequired = false;
  state.isPasswordRestored = mustChangePassword(state.accessToken);
}

function resetAnonymousLogin(state: AuthState) {
  Cookies.remove(authCodeCookie);
  state.anonymousLoginInfo.authCode = null;
  state.anonymousLoginInfo.phone = null;
  state.anonymousLoginInfo.isLoggedIn = false;
}

function mustChangePassword(accessToken: string) {
  if (accessToken) {
    var decoded: DecodedToken = jwtDecode(accessToken);
    var mustChangePassword: boolean = decoded.orderme_mustChangePassword;
    if (!mustChangePassword) {
      return false;
    }

    return mustChangePassword;
  }

  return false;
}

export const {
  logout,
  login,
  logoutAnonymous,
  forceLoginAgain,
  resetLoginRequired,
  setRememberMe,
  reloadTokenFromStorage,
  removeLoginError,
} = authSlice.actions;
export default authSlice.reducer;
